import { useState } from "react";
import { base44 } from "@/api/base44Client";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input"; // Added Input import
import { Alert } from "@/components/ui/alert"; // Added Alert import
import { Upload, CheckCircle, AlertCircle, Loader2, Copy } from "lucide-react";
import { motion } from "framer-motion";

export default function Upload3DModelPage() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedUrl, setUploadedUrl] = useState("");
  const [error, setError] = useState("");

  // REMOVED: Preview URL and model-viewer script loading

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Check file type
      if (!selectedFile.name.endsWith('.glb') && !selectedFile.name.endsWith('.gltf')) {
        setError("Please upload a .glb or .gltf file");
        return;
      }
      
      // Check file size (max 10MB)
      if (selectedFile.size > 10 * 1024 * 1024) {
        setError("File size must be less than 10MB");
        return;
      }

      setFile(selectedFile);
      setError("");
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError("Please select a file first");
      return;
    }

    setUploading(true);
    setError("");

    try {
      // Upload file using base44 Core integration
      const result = await base44.integrations.Core.UploadFile({ file });
      
      setUploadedUrl(result.file_url);
      
    } catch (err) {
      console.error("Upload error:", err);
      setError("Upload failed: " + err.message);
    } finally {
      setUploading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(uploadedUrl);
    const button = document.getElementById('copy-button');
    if (button) {
      button.textContent = '✓ Copied!';
      setTimeout(() => button.textContent = 'Copy URL', 2000);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-5xl font-black text-gray-900 mb-4">
            Upload 3D Model (.GLB)
          </h1>
          <p className="text-xl text-gray-600">
            Upload your LCTX Tyre Changer 3D model file
          </p>
        </motion.div>

        {/* Main Upload Card */}
        <Card className="shadow-2xl">
          <CardContent className="p-8">
            
            {/* File Input */}
            <div className="mb-8">
              <label className="block text-lg font-bold text-gray-900 mb-4">
                Select GLB/GLTF File
              </label>
              <div className="relative">
                <input
                  type="file"
                  accept=".glb,.gltf"
                  onChange={handleFileChange}
                  className="hidden"
                  id="file-upload"
                />
                <label
                  htmlFor="file-upload"
                  className="flex flex-col items-center justify-center w-full h-64 border-4 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-red-500 transition-all bg-gray-50 hover:bg-red-50"
                >
                  <Upload className="w-16 h-16 text-gray-400 mb-4" />
                  <span className="text-xl font-semibold text-gray-700">
                    {file ? file.name : "Click to select file"}
                  </span>
                  <span className="text-sm text-gray-500 mt-2">
                    GLB or GLTF format, max 10MB
                  </span>
                </label>
              </div>
            </div>

            {/* File Info */}
            {file && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 bg-blue-50 border-2 border-blue-200 rounded-lg p-6"
              >
                <div className="flex items-start gap-4">
                  <CheckCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                  <div className="flex-1">
                    <h3 className="font-bold text-blue-900 text-lg mb-2">File Selected</h3>
                    <div className="space-y-1 text-sm text-blue-800">
                      <p><strong>Name:</strong> {file.name}</p>
                      <p><strong>Size:</strong> {(file.size / 1024 / 1024).toFixed(2)} MB</p>
                      <p><strong>Type:</strong> {file.type || 'model/gltf-binary'}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
            
            {error && (
              <Alert className="mb-6 bg-red-50 border-red-300">
                <AlertCircle className="h-5 w-5 text-red-600" />
                <p className="text-red-900 font-semibold">{error}</p>
              </Alert>
            )}

            {/* Upload Button */}
            <Button
              onClick={handleUpload}
              disabled={uploading || !file}
              className="w-full h-14 text-lg font-bold bg-red-600 hover:bg-red-700 disabled:bg-gray-400"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-6 h-6 mr-3 animate-spin" />
                  <span>Uploading...</span>
                </>
              ) : (
                <>
                  <Upload className="w-6 h-6 mr-3" />
                  <span>Upload 3D Model</span>
                </>
              )}
            </Button>
            
            {/* Upload Result */}
            {uploadedUrl && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-8 bg-green-50 border-2 border-green-200 rounded-lg p-6"
              >
                <h3 className="text-xl font-bold text-green-900 mb-4">
                  ✅ Upload Successful!
                </h3>
                <p className="text-green-800 mb-4">
                  Your 3D model has been uploaded. Please copy the URL below and send it to me:
                </p>
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value={uploadedUrl}
                    className="flex-grow bg-white"
                  />
                  <Button id="copy-button" onClick={copyToClipboard} className="flex items-center gap-2">
                    <Copy className="w-4 h-4" />
                    Copy URL
                  </Button>
                </div>
              </motion.div>
            )}
            {/* REMOVED PREVIEW SECTION */}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}